import {useLocation, useNavigate} from 'react-router-dom';

import {useQueryClient} from '@tanstack/react-query';
import {removeAuthTokens} from 'infra/http/utils';
import {
	AuthorizationsEnum,
	ContractTypeEnum,
	ExamBookingEnum,
	GuidesEnum,
	OdontoBookingEnum,
	PlanTypeEnum,
	SignInEnum,
	UserTypeEnum,
} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {formatText, getOnlyFirstWord, getUserInfoFromToken, isOfLegalAge} from 'presentation/utils';

import {clearLocalStorageFeatureFlags} from 'infra/http/utils/clear-local-storage-feature-flags';
import {useSelectedBeneficiaryStore} from 'presentation/pages/others/selection-flow/store/useSelectedBeneficiaryStore';
import {useLocationStore} from '../store/locationStore';
import {FlowType} from '../store/locationStore/constants';
import {LocationType} from '../store/locationStore/types';
import {useThemeStore} from 'presentation/store/useThemeStore';
import {isFuture, parse, isPast} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import {usePpoNetworkStore} from 'presentation/modules/guide/shared/store/guideStore/usePpoNetworkStore';
import {clearCongenereEligibilityStorage} from 'infra/http/utils/clear-congenere-eligibility-storage';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';

export function useAccountInformation() {
	const {beneficiary, sidebar, accesses, logged_user, planBeneficiaries, companyPortal} =
		useAccountDataStore(store => store.accountData);
	const {
		tipoPlanoC,
		tipoContratacao,
		nmUsuarioC,
		nmSocialUsuarioC,
		dtCarenciaC,
		dtAdesaoC,
		dtCarenciaOdontoC,
	} = beneficiary;
	const {ppoProduct, accessConcierge} = accesses;

	const queryClient = useQueryClient();
	const cdPessoaC = getUserInfoFromToken().cdPessoa;

	const getLocationTypeFromPath = (pathname: string): LocationType => {
		if (pathname.includes(AuthorizationsEnum.REQUEST)) return FlowType.AUTHORIZATION;
		if (pathname.includes(OdontoBookingEnum.ODONTO_BOOKING_INITIAL)) return FlowType.ODONTO;
		if (pathname.includes(ExamBookingEnum.EXAM_BOOKING_INITIAL)) return FlowType.EXAM;
		if (pathname.includes(GuidesEnum.SERVICE_NETWORK_ODONTO)) return FlowType.ODONTO_GUIDE;
		if (pathname.includes(GuidesEnum.SERVICE_NETWORK_HEALTH)) return FlowType.HEALTH_GUIDE;
		return FlowType.DEFAULT;
	};

	const {pathname} = useLocation();

	const locationType = getLocationTypeFromPath(pathname);

	const isInGracePeriod =
		(dtCarenciaC && isFuture(parse(dtCarenciaC, 'P', new Date(), {locale: ptBR}))) ||
		(dtCarenciaOdontoC && isFuture(parse(dtCarenciaOdontoC, 'P', new Date(), {locale: ptBR})));

	const isWithinEnrollmentPeriod =
		!!dtAdesaoC && isPast(parse(dtAdesaoC, 'P', new Date(), {locale: ptBR}));

	const getLocation = useLocationStore(state => state.getLocation);
	const currentLocation = getLocation(cdPessoaC, locationType);
	const currentAppointmentLocation = getLocation(cdPessoaC, FlowType.DEFAULT);

	const {isClinipam, isCCG, isGndiMinas, isHAP} = useHostName();
	const navigate = useNavigate();

	const resetSelectedBeneficiary = useSelectedBeneficiaryStore(
		state => state.resetSelectedBeneficiary,
	);

	const resetPpoNetworkType = usePpoNetworkStore(state => state.resetPpoNetworkType);
	const resetFacialBiometrics = useFacialBiometricsStore(state => state.resetFacialBiometrics);

	const userName = nmSocialUsuarioC || nmUsuarioC;
	const firstNameBeneficiary = getOnlyFirstWord(formatText(userName));
	const {flPlanoSomenteHospitalar, flAdministradora, flPad, flNascerBem, flViverBem} = sidebar;
	const businessPlan = tipoContratacao === ContractTypeEnum.EMPRESARIAL;
	const isHolder = beneficiary.tipoUsuarioC === UserTypeEnum.HOLDER;

	const healthOnly = tipoPlanoC === PlanTypeEnum.HEALTH;
	const odontoOnly = tipoPlanoC === PlanTypeEnum.ODONTO;
	const healthOdonto = tipoPlanoC === PlanTypeEnum.HEALTH_ODONTO;

	const canChangePassword = String(cdPessoaC) === String(logged_user.cdPessoa);
	const canChangeAddress = isHolder && String(cdPessoaC) === String(logged_user.cdPessoa);

	const hideHealthHospitalComponent = healthOnly && flPlanoSomenteHospitalar;

	const isResponsibleWithoutBenefits = beneficiary?.flTitularSemDireito;

	const isLegalAge = isOfLegalAge(beneficiary?.dtNascimentoUsuarioC);

	const canChangeAccess =
		String(cdPessoaC) === String(logged_user.cdPessoa) &&
		isLegalAge &&
		planBeneficiaries.length > 1;

	const isIndividualPlan = flPad || (!businessPlan && !flAdministradora);

	const isPPOPlan = ppoProduct;
	const isB2BAccess = !!companyPortal;
	const isOurDoctorPlan = accesses?.ourDoctorProductBeneficiary ?? false;

	const isEarlyAccess =
		!!beneficiary?.dtAdesaoC &&
		isFuture(parse(beneficiary?.dtAdesaoC, 'P', new Date(), {locale: ptBR}));

	const resetTheme = useThemeStore(state => state.resetTheme);
	const handleLogout = () => {
		resetPpoNetworkType();
		removeAuthTokens();
		clearLocalStorageFeatureFlags();
		clearCongenereEligibilityStorage();
		resetFacialBiometrics();
		resetTheme();
		navigate(SignInEnum.SIGN_IN, {state: {goToSignInState: true}});
		queryClient.clear();
		return null;
	};

	return {
		beneficiary,
		flPlanoSomenteHospitalar,
		businessPlan,
		healthOnly,
		odontoOnly,
		healthOdonto,
		canChangePassword,
		handleLogout,
		userName,
		firstNameBeneficiary,
		resetBeneficiary: resetSelectedBeneficiary,
		currentAppointmentLocation,
		currentLocation,
		hideHealthHospitalComponent,
		isCCG,
		isHAP,
		isClinipam,
		isGndiMinas,
		flAdministradora,
		flPad,
		flNascerBem,
		flViverBem,
		accesses,
		isHolder,
		canChangeAddress,
		isResponsibleWithoutBenefits,
		planBeneficiaries,
		isLegalAge,
		isIndividualPlan,
		isPPOPlan,
		isB2BAccess,
		accessConcierge,
		isOurDoctorPlan,
		isEarlyAccess,
		isInGracePeriod,
		isWithinEnrollmentPeriod,
		canChangeAccess,
	};
}
